import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQueryClient } from 'react-query';
import usePostsQuery from '../../../components/api/queries/usePostsQuery';
import useUserQuery from '../../../components/api/queries/useUserQuery';
import { useGetLatestPostsCount } from '@pocapan/shared/domains/lounge/ui/hooks/useGetLatestPostsCount';
import { useLikeLoungeMessage } from '@pocapan/shared/domains/lounge/ui/hooks/useLikeLoungeMessage';
import { useFollowUser } from '@pocapan/shared/domains/user/ui/hooks/useFollowUser';
import type { components } from '@pocapan/shared/types/schema';
import saveData from '@pocapan/shared/utils/saveData';

const PER_PAGE = 20;
type PostPreview = components['schemas']['PostPreview'];

export const useLoungeChat = (categoryGroup = 'community') => {
  const user = useUserQuery();
  const queryClient = useQueryClient();
  const [selectedArtist, setSelectedArtist] = useState<string>('');
  const [countryCode] = useState<string | undefined>(
    (typeof window !== 'undefined' && localStorage.getItem('i18nextLng')) || undefined
  );

  const { data, fetchNextPage, hasNextPage, isFetching, isFetchingNextPage } = usePostsQuery({
    country: countryCode || user?.country,
    artistId: undefined,
    userProfileId: undefined,
    photoCardId: undefined,
    anonymous: undefined,
    perPage: PER_PAGE,
    onlyFollowers: false,
    categoryGroup
  });

  const latestPostId =
    data?.pages[0]?.posts?.[0]?.postId ||
    (localStorage.getItem(`latestPostId-${categoryGroup}`) as string | null);
  const isTempLatestPostId =
    typeof latestPostId === 'string' && latestPostId.toLowerCase().includes('temp');
    
  const { data: latestPostsCount } = useGetLatestPostsCount(
    isTempLatestPostId ? undefined : latestPostId || undefined,
    categoryGroup,
    !isTempLatestPostId
  );
  const { mutateAsync: likeLoungeMessageMutate } = useLikeLoungeMessage();
  const { mutateAsync: followUserMutate } = useFollowUser();

  const getQueryKey = useCallback(() => {
    return ['Posts', countryCode || user?.country, null, null, null, null, false, categoryGroup];
  }, [countryCode, user?.country, categoryGroup]);

  const posts = useMemo(() => {
    const pages = data?.pages ?? [];
    const flat = pages.flatMap((page) => (page as { posts: PostPreview[] }).posts.map((p) => p));
    return flat.reverse();
  }, [data]);

  const myProfileId = user?.baseUserProfile?.userProfileId;

  useEffect(() => {
    setSelectedArtist(localStorage.getItem('artist') || '');
  }, []);

  useEffect(() => {
    const postId = data?.pages[0]?.posts?.[0]?.postId;
    const isTemp = typeof postId === 'string' && postId.toLowerCase().includes('temp');
    if (!isTemp) {
      saveData(`latestPostId-${categoryGroup}`, postId || '');
    }
  }, [data, categoryGroup]);  

  useEffect(() => {
    return () => {
      queryClient.removeQueries(['Posts'], { exact: false });
    };
  }, [queryClient]);

  const handleLikeLoungeMessage = async (postId: string) => {
    if (!postId) return;
    try {
      await likeLoungeMessageMutate({
        postId,
        queryKey: getQueryKey()
      });
    } catch (error) {
      console.error(error);
    }
  };

  const handleFollowUser = async (myProfileId: string, userProfileId: string) => {
    if (!myProfileId || !userProfileId) return;
    try {
      await followUserMutate({
        myProfileId,
        userProfileId
      });
    } catch (error) {
      console.error(error);
    }
  };

  return {
    isFetching,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    posts,
    myProfileId,
    profileImageUrl: user?.baseUserProfile?.thumbnailUrl,
    latestPostsCount,
    handleLikeLoungeMessage,
    handleFollowUser,
    selectedArtist,
    setSelectedArtist
  };
};
